import { useState, useEffect, useRef } from "react";
import { TDDDocument } from "../../data/tdd/tddDocs";
import { TDDDocumentHeader } from "./TDDDocumentHeader";
import { TDDSection } from "./TDDSection";
import { useParallax } from "../../hooks/useParallax";

interface TDDDocumentPanelProps {
  tddId: string;
  title: string;
  document: TDDDocument;
  currentIndex: number;
  onOpenContact?: () => void;
}

export function TDDDocumentPanel({
  tddId,
  title,
  document,
  currentIndex,
  onOpenContact,
}: TDDDocumentPanelProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [key, setKey] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  
  // Watermark parallax: 1-3px movement
  const watermarkParallax = useParallax(0.015);

  useEffect(() => {
    // Exit animation
    setIsExiting(true);
    setIsVisible(false);
    
    const exitTimer = setTimeout(() => {
      setIsExiting(false);
      setKey((prev) => prev + 1);
      // Enter animation
      setIsVisible(true);
    }, 160);

    return () => clearTimeout(exitTimer);
  }, [tddId]);

  return (
    <div
      ref={panelRef}
      className={`flex-1 transition-all duration-[240ms] ${
        isVisible && !isExiting
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-[8px]"
      }`}
      style={{
        transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
        transitionProperty: "opacity, transform",
      }}
    >
      <div className="h-full overflow-visible lg:overflow-y-auto lg:max-h-[calc(100vh-200px)]">
        {/* Document Frame - Design.md spec */}
        <div className="relative bg-[#0F141A] border border-[#1E2630] rounded-sm px-4 sm:px-6 md:px-12 py-6 md:py-12 tdd-document-pane" style={{
          boxShadow: "inset 0 0 20px rgba(24, 224, 200, 0.06), 0 0 20px rgba(0,0,0,0.3)",
        }}>
          {/* Noise overlay - 2-3% opacity */}
          <div 
            className="absolute inset-0 pointer-events-none rounded-sm opacity-[0.025]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='1'/%3E%3C/svg%3E")`,
              backgroundSize: '200px 200px',
            }}
          />
          
          {/* Vignette - top + bottom */}
          <div className="absolute inset-0 pointer-events-none rounded-sm" style={{
            background: "radial-gradient(ellipse 100% 50% at 50% 0%, transparent 0%, transparent 40%, rgba(0,0,0,0.15) 100%), radial-gradient(ellipse 100% 50% at 50% 100%, transparent 0%, transparent 40%, rgba(0,0,0,0.15) 100%)",
          }} />
          
          {/* Content wrapper with max-width clamp - Design.md: max-w-[760px] */}
          <div className="relative max-w-[760px] mx-auto">
          {/* Document Header */}
          <TDDDocumentHeader
            tddId={tddId}
            title={title}
            lastReview={document.lastReview}
            documentStatus={document.status}
            scope={document.scope}
          />

          {/* Divider - teal tinted, 15-20% opacity */}
          <div className="h-[1px] w-full my-6 tdd-divider-enter" style={{
            background: "linear-gradient(to right, transparent, rgba(24, 224, 200, 0.18), transparent)",
          }} />

          {/* WHAT IT DOES Section - 24px baseline spacing */}
          <div className="mb-6">
            <TDDSection title="WHAT IT DOES">
              <p className="my-4 md:my-6 leading-relaxed sm:leading-relaxed">{document.what}</p>
            </TDDSection>
          </div>

          {/* Divider - teal tinted */}
          <div className="h-[1px] w-full my-6 tdd-divider-enter" style={{
            background: "linear-gradient(to right, transparent, rgba(24, 224, 200, 0.18), transparent)",
          }} />

          {/* SUMMARY Section - 24px baseline spacing */}
          <div className="mb-6">
            <TDDSection title="SUMMARY">
              <p className="my-4 md:my-6 leading-relaxed sm:leading-relaxed whitespace-pre-line">{document.summary}</p>
            </TDDSection>
          </div>

          {/* Divider - teal tinted */}
          <div className="h-[1px] w-full my-6 tdd-divider-enter" style={{
            background: "linear-gradient(to right, transparent, rgba(24, 224, 200, 0.18), transparent)",
          }} />


          {/* CAPABILITIES Section - Design.md: spec rows with dividers */}
          <div className="mb-6">
            <TDDSection title="CAPABILITIES">
              <div className="mt-4">
                {document.capabilities.map((capability, index) => (
                  <div key={index}>
                    <div
                      className={`group relative bg-transparent px-0 py-2 text-white/80 text-sm sm:text-[14px] leading-relaxed sm:leading-relaxed transition-all duration-[140ms] ease-out hover:text-white/90 ${
                        isVisible && !isExiting ? "tdd-capability-enter" : "opacity-0"
                      }`}
                      style={{
                        animationDelay: `${index * 60}ms`,
                      }}
                    >
                      {/* Square teal indicator + text */}
                      <div className="flex items-start gap-3">
                        <div className="mt-1.5 w-1.5 h-1.5 bg-[rgba(24,224,200,0.6)] group-hover:bg-[rgba(24,224,200,0.9)] transition-colors duration-[140ms] flex-shrink-0" />
                        <span>{capability}</span>
                      </div>
                    </div>
                    {/* Divider between items - except last */}
                    {index < document.capabilities.length - 1 && (
                      <div className="h-[1px] w-full my-2" style={{
                        background: "linear-gradient(to right, transparent, rgba(255,255,255,0.08), transparent)",
                      }} />
                    )}
                  </div>
                ))}
              </div>
            </TDDSection>
          </div>

          {/* Contact CTA */}
          <div className="mt-10 rounded-md border border-white/10 bg-black/20 px-4 py-4">
            <p className="text-sm font-semibold text-thema-text mb-1">Want to know more?</p>
            <p className="text-xs text-thema-text-soft/85 mb-3">
              For technical context, research discussion, or implementation details, contact the team.
            </p>
            {onOpenContact && (
              <button
                type="button"
                onClick={() => {
                    window.location.href = "mailto:aamir@thema.financial";
                  }}
                className="inline-flex items-center rounded-md bg-thema-royal-blue px-3 py-1.5 text-xs font-semibold text-white shadow-[0_6px_24px_rgba(77,183,247,0.35)] transition hover:bg-thema-royal-blue/90"
              >
                Contact us →
              </button>
            )}
          </div>

          {/* Footer with pagination marker - Design.md: bottom-left */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="flex items-center justify-between">
              {/* Pagination marker - bottom-left as per Design.md */}
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">
                PAGE {String(currentIndex + 3).padStart(3, "0")}
              </p>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">
                DOCUMENT REF: /tdd/{tddId.toLowerCase()}/005
              </p>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

